import Image from "next/image";
import Link from "next/link";
import { useState } from "react";
import {
  Accordion,
  AccordionBody,
  AccordionHeader,
  AccordionItem,
  Button,
  Col,
  Container,
  Row,
} from "reactstrap";

import classes from "@/styles/open-account-offer/open-account-offer-main.module.css";
import { openAccountOffer } from "@/utils/content";
import { woImg1 } from "@/utils/images";
import { routes } from "@/utils/routes";
import { useRouter } from "next/router";
import FAQAccordion from "../utils/faq-accordion";

const imageSource =
  "https://cbpstaticfiles.blob.core.windows.net/sv-app/offers/open-account-offer/";

const howItWorks = [
  {
    title:
      "Navigate to offers page by clicking on you name, and click on NEW ACCOUNT OFFER.",
    imageUrl: `${imageSource}step-1.png`,
  },
  {
    title: "Click on CLAIM OFFER to claim your offer.",
    imageUrl: `${imageSource}step-2.png`,
  },
  {
    title:
      "If have not made a deposit click on DEPOSIT NOW to add a card and make a new deposit in your preferable currency.",
    imageUrl: `${imageSource}step-3.png`,
  },
  {
    title:
      "If have already made a deposit earlier you can choose to make a new deposit or continue with your earlier deposit amount.",
    imageUrl: `${imageSource}step-3-1.png`,
  },
  {
    title:
      "Once you have successfully claimed your offer based on selected amount and currency you should see your offer is now active . You can navigate to offers page by clicking your name in top left corner > offers available.",
    imageUrl: `${imageSource}step-4.png`,
  },
  {
    title: "You should see your bonus amount.",
    imageUrl: `${imageSource}step-5.png`,
  },
  {
    title: "Track your spending by navigating to the offers page.",
    imageUrl: `${imageSource}step-6.png`,
  },
  {
    title:
      "Once you have spent more than or equal to spending amount. Bonus will me made available for you to use in next 24 hours.",
    imageUrl: `${imageSource}step-7.png`,
  },
  {
    title:
      "Your offer status should say redeemed and bonus amount is now available for you to print labels.",
    imageUrl: `${imageSource}step-8.png`,
  },
  {
    title: "Bonus credit should show on transaction history page.",
    imageUrl: `${imageSource}step-9.png`,
  },
];

const OPEN_ACCOUNT_FAQ = [
  {
    title: "Can I use my bonus on my first shipment?",
    desc: "No, That's not possible as the spent rule has not been met.",
  },
  {
    title: "How soon after I sign up can I begin shipping?",
    desc: "You can begin shipping immediately.",
  },
  {
    title: "How much should I spend to be eligible?",
    desc: "Spend 5x the qualifying deposit to make your bonus amount usable.",
  },
  {
    title: "Is there a minimum deposit?",
    desc: "Yes, Make a qualifying deposit of $10 or more",
  },
  {
    title: "What are Shipvista.com’s default carrier accounts?",
    desc: "These are carrier accounts that are already in the shipvista platform, Shipvista.com fully manages the purchase of labels via a single payor and provides full support to the shipper on behalf of the merchant. Shipvista.com’s default carrier accounts include; Canada Post, UPS, USPS, and CanPar Express among others.",
  },
];

export default function OpenAccountOfferMain() {
  const content = openAccountOffer.en;
  const router = useRouter();

  const [open, setOpen] = useState("1");
  //@ts-ignore
  const toggle = (id) => {
    if (open === id) {
      //@ts-ignore
      setOpen();
    } else {
      setOpen(id);
    }
  };

  const getCapitalLetters = (str: string) =>
    str
      .split("")
      .filter(
        (char) => char === char.toUpperCase() && char !== char.toLowerCase()
      )
      .join("");

  const getDateTime = (addDays: boolean) => {
    let now = new Date();

    if (addDays) {
      now.setDate(now.getDate() + 30);
    }

    const dateOptions: any = {
      month: "long",
      day: "numeric",
      year: "numeric",
    };
    const timeOptions: any = {
      hour: "numeric",
      minute: "numeric",
      hour12: false,
    };
    const dateStr = now.toLocaleDateString("en-US", dateOptions);
    const timeStr = now.toLocaleTimeString("en-US", timeOptions);
    const timeZoneAbbr = getCapitalLetters(new Date().toString().split("(")[1]);
    const dateTimeStr = `${timeStr} ${timeZoneAbbr} on ${dateStr}`;

    return dateTimeStr;
  };

  return (
    <main className="py-5">
      <Container>
        <section id="offer">
          <div className={`${classes.productInfo} container`}>
            <div className={classes.productInfoText}>
              <h2>Welcome Offer</h2>
              <p>
                <strong>NEW CUSTOMER WELCOME OFFER</strong>
              </p>
              <hr />
              <p style={{ fontFamily: "verdana", color: "#000" }}>
                Open an account today and claim your <b>100% Deposit Match</b>{" "}
                Bonus of Up to <b>$1000</b> - Get a complimentary deposit
                matched up to <b>$1000</b> in shipping credit on SIGN UP,
                approx. <b>300 Free shipments*</b> in most routes.
              </p>
              <p>
                These New Open Account Offer Terms and Conditions apply to new
                customers who open an account from <b>{getDateTime(false)}</b>.
                If you opened your account before this date and require a copy
                of the terms and conditions which apply to you then please{" "}
                <Link href={routes.contact}>contact us</Link>.
              </p>

              <span style={{ fontFamily: "Segoe UI, sans-serif" }}>
                <p className="text-muted">
                  To qualify for your match bonus, ShipVista.com recommends you
                  check these Main offer terms:
                </p>
                <ol style={{ color: "#000" }}>
                  <li>
                    Available to customers residing in <b>Canada (CA)</b> and
                    the <b>United States of America (USA)</b> and registering a
                    new account with ShipVista.com on or after{" "}
                    <b>{getDateTime(false)}</b>.
                  </li>
                  <li>
                    The amount of shipping Credits that can be claimed is capped
                    at <b>$1000 either in USD or CAD</b> based on your deposit
                    currency.
                  </li>
                  <li>
                    <b>Bonus qualification period:</b> Make first or multiple
                    deposits 7 days before bonus activation.
                  </li>
                  <li>
                    You must claim the offer within 30 days of registering your
                    account (The expiration date of the offer).
                  </li>
                  <li>
                    Once Claimed, the shipping credit or bonus never expires.
                  </li>
                  <li>
                    <b>Add Funds/Deposit Requirement:</b> Make a qualifying
                    deposit of $10 or more and receive a 100% Deposit Match
                    Bonus of up to $1000. If your qualifying deposit is $1000 or
                    more, then you will only qualify for a bonus cap of $1000.
                  </li>
                  <li>
                    Your qualifying deposit is your first deposit or the largest
                    payment made 7 days prior to claiming the bonus. In this New
                    Open Account offer version, You have the option to choose or
                    elect your deposit amount to claim the bonus.
                  </li>
                  <li>
                    <b>Spending requirement:</b> Spend 5x (Times) the qualifying
                    deposit to make your bonus amount usable. (Only Postage
                    purchased via Shipvista.com’s default carrier accounts
                    including Canada Post, UPS, USPS, and CanPar Express among
                    others. are counted toward the spending requirement. Postage
                    or Shipment labels from other sources or your own connected
                    accounts(BYOA) are not considered for this offer.
                  </li>
                  <li>
                    Your shipping Credits will be held in your account balance
                    and are non-withdrawable. To release your shipping Credits
                    you must spend 5 times the amount claimed.
                  </li>
                  <li>
                    <b>Match the Highest Deposit Amount:</b> Make multiple
                    deposits prior to claiming the bonus and we will choose your
                    largest deposit.
                  </li>
                  <li>
                    Make first or multiple deposits 7 days before bonus
                    activation.
                  </li>
                  <li>
                    Once you claim your offer, we will send a notification when
                    the deposit bonus is available for use.
                  </li>
                  <li>This sign-up bonus is only offered to new customers.</li>
                  <li>
                    Your shipping Credits will normally be available to use
                    within 24 hours of the qualifying spending requirement is
                    met.
                  </li>
                  <li>Maximum bonus capped at $1000.</li>
                  <li>Valid for one-time use only.</li>
                  <li>
                    Employees, officers, and directors of ShipVista.com, its
                    promotional or other agencies, licensees and licensors,
                    service providers, and any other associated or affiliated
                    companies shall not be eligible for the promotion. The same
                    terms shall apply to the direct families of such persons.
                  </li>
                  <li>
                    ShipVista.com may, at any time, make minor amendments to
                    this promotion to correct typographical errors or to improve
                    on clarity or customer experience and may cancel this
                    promotion for legal or regulatory reasons.
                  </li>
                </ol>
              </span>
              <p style={{ fontFamily: "verdana", color: "#000" }}>
                <b>NB:</b> You can Add deposits or fund your ShipVista account
                with an unlimited amount of funds to use in buying postage and
                other shipping accessories. There&#39;s no deposit or add funds
                limit. and your money never expires.
              </p>
              <Button onClick={() => router.push(routes.register)}>
                Sign Up Now to Claim Offer
              </Button>
            </div>
          </div>
          <div
            className={`${classes.howSection} container`}
            style={{ fontFamily: "verdana", color: "#000" }}
          >
            <h3>How the offer works:</h3>
            <p>
              To qualify for your bonus, please follow these simple
              instructions:
            </p>

            <p>
              As part of signing up, first-time customers are entitled to 100%
              match bonuses on initial deposits (qualifying deposits of $10 or
              more) up to $1000 in Shipping Credits.
            </p>

            <p>
              To qualify for your match bonus, ShipVista.com recommends you
              check these offer terms:
            </p>
            <p>How the offer works:</p>
            <p>Sign up, deposit $10 or more, and then claim the offer.</p>
            <p>
              <b>Add Funds/Deposit Requirement:</b> Make a qualifying deposit of
              $10 or more and receive a 100% Deposit Match Bonus of up to $1000.
              If your qualifying deposit is $1000 or more, then you will only
              qualify for a bonus of $1000.
            </p>
            <p>
              Your qualifying deposit is your first deposit or the largest
              deposit made <b>7 days</b> prior to claiming the bonus.
            </p>
            <p>
              <b>Match the Highest Deposit Amount:</b> Make multiple deposits
              prior to claiming the bonus and we will choose your largest
              deposit. Alternatively, in the new bonus offer version you can
              choose or elect which deposit to use as the Highest deposit amount
              to claim the bonus base on.
            </p>
            <h5>Example 1) - Single Deposit:</h5>
            <ol>
              <li>
                You Deposited $10; we will match it 100%, that is, We will give
                you another $10 in this offer. You Balance will be $20 =( i.e.
                $10 of your money and a $10 bonus offer).
              </li>
              <li>
                You Deposited $120 we will match it 100%, that is, We will give
                you another $120 in this offer. You Balance will be $240 =(i.e.
                $120 your money and a $120 bonus offer).
              </li>
              <li>
                You Deposited $200 we will match it 100%, that is, We will give
                you another $200 in this offer. You Balance will be $400 =( i.e.
                $200 your money and a $200 bonus offer).
              </li>
              <li>
                <b>
                  You Deposited $3000 we will match it to $1000(The offer is
                  Capped at $1000), that is, We will give you $1000 in this
                  offer. You Balance will be $4000 =(i.e. $3000 of your money
                  and a $1000 bonus offer).
                </b>
              </li>
              <li>
                You Deposited $1200 we will match it to $1000(The offer is
                Capped at $1000), that is, We will give you $1000 in this offer.
                You Balance will be $2200 =(i.e. $1200 your money and a 1000
                bonus offer).
              </li>
            </ol>
            <p>
              <b>NB:</b> You can Add an unlimited amount of funds to your
              ShipVista account to use in buying postage and other shipping
              accessories. There&#39;s no deposit limit.
            </p>
            <h5>Example 2)</h5>
            <p>
              <b>Multiple Deposit - 7 days before claiming your offer:</b> If
              you made 1 or more deposits prior to claiming the bonus, we will
              use the 7 days rule as follows, we will choose and Match the
              Highest Deposit Amount; Nevertheless, in the new bonus offer
              version, the client can choose the deposit amount to claim the
              bonus base on.
            </p>
            <ol>
              <li>
                You added funds as follows $30, $50, $10, and $60, we will
                choose and Match the Highest Deposit Amount, In this case, your
                deposit will be $60. We will match it 100% since it is below the
                $1000 capped offer amount. We will give you another $60 in this
                offer which will reflect in your account Balance.
              </li>

              <li>
                You added funds as follows $50, $10, $200, and $60, In this
                case, your highest deposit will be $200. We will match it 100%
                since it is within the $1000 capped offer amount. We will give
                you another $200 in this offer which will reflect in your
                account Balance.
              </li>

              <li>
                You added funds as follows $500, $1400, $170, and $2400, In this
                case, your highest deposit will be $2400. We will match it to
                only up to $1000(The offer is Capped at $1000), that is, We will
                give you $1000 in this offer which will reflect in your account
                Balance.
              </li>
            </ol>
            <p>
              <b>NB:</b> In this New Open Account offer version, You have the
              option to choose or elect your deposit amount to claim the bonus
              offer.{" "}
            </p>
            <p>
              <b>Spending requirement:</b> You must Spend 5x (Times) the bonus
              amount to make your Shipping Credit amount usable.
            </p>
            <h5>Example 3)</h5>
            <p>5x Spending rules - Shipping credit offer to come Usable.</p>
            <ol>
              <li>
                We Gave you a $100 bonus offer, you will need to spend 5X that
                amount, that is, you will need to spend $500 =($100 * 5) on
                postage for our $100 shipping credit offer to become useable.
              </li>

              <li>
                We Gave you a $200 bonus offer, you will need to spend 5X that
                amount, that is, you will need to spend $1000 =($200 * 5) on
                postage for our $200 shipping credit offer to become useable.
              </li>
              <li>
                We Gave you a $1000 bonus offer, you will need to spend 5X that
                amount, that is, you will need to spend $5000 =($1000 * 5) on
                postage for our $1000 shipping credit offer to become useable.
              </li>
            </ol>
            <p>
              <b>NB:</b> Remember; You have the option to choose or elect your
              highest deposit amount to claim the bonus based on. If you feel
              that the $1000 shipping credit offer is too much for your business
              then use the elect deposit amount option. Nevertheless, our
              records show that 99.97% of users always maximize the bonus offer
              caps.
            </p>
            <p>
              Once Claimed, the shipping credit or bonus offer amount never
              expires. It stays in your Shipvista account until it&#39;s used to
              purchase Postage (Carrier Shipping Labels) or pay for shipment
              charges and accessories.
            </p>
            <p>
              <b>Note:</b> approx. 300 Free shipments* in most routes in this
              offer. Works with USPS shipments, UPS, CANADA POST, CANPAR, and
              many carriers on our platform.
            </p>
            <ul>
              <li>
                Bonus qualification period: Make first or multiple deposits 7
                days before bonus activation.
              </li>
              <li>
                Once you claim your offer, we will send a notification when the
                deposit bonus is available for use.
              </li>
              <li>This sign-up bonus is only offered to new customers.</li>
              <li>Maximum bonus capped at $1000.</li>
              <li>Valid for one-time use only.</li>
            </ul>
            <br />
            <h4>How the offer works:</h4>
            <p>
              Sign up, make a qualifying deposit of $10 or more, and then claim
              the offer. <br /> See Significant & Full Terms and Conditions
              below.
            </p>
            <h5>Significant Terms and Conditions</h5>
            <ul>
              <li>
                Available to customers residing in Canada and the United States
                of America and registering a new account with ShipVista.com on
                or after {getDateTime(false)}.
              </li>
              <li>
                A deposit (minimum $10) is required to participate in this
                offer. Once your deposit is completed, claim the offer and place
                a qualifying buy postage order (Create a Shipment).
              </li>
              <li>
                You must claim the offer within 30 days (The expiration date of
                the offer) of registering your account.
              </li>
              <li>Your Claimed shipping credit or bonus never expires.</li>
              <li>For further details, see full Terms and Conditions.</li>
              <li>
                Your shipping Credits are non-withdrawable, and shipping Credits
                cannot be used on certain products, offers/promotions, and
                postage types. See full Terms and Conditions for details.
              </li>
            </ul>
            <h5>Releasing your bonus Credits:</h5>
            <p>Full Terms and Conditions</p>
            <p>
              <b>Qualifying</b>
            </p>
            <ul>
              <li>
                Offer available to customers residing in Canada and the United
                States of America registering a new account with ShipVista.com
                on or after {getDateTime(false)}. All customer offers are
                limited to one per person.
              </li>
              <li>
                A deposit (minimum $10) is required to participate in this
                offer. The deposit must be made in the 7 days before you claim
                the offer.
              </li>
              <li>
                You must claim your offer within 30 days of registering your
                account.
              </li>
              <li>
                The amount of shipping Credits that can be claimed is capped at
                $1000 either in USD or CAD based on deposit currency.
              </li>
            </ul>
            <p>
              <b>Releasing your Bet Credits:</b>
            </p>
            <ul>
              <li>
                Your shipping Credits will be held in your account balance and
                are non-withdrawable. To release your shipping Credits you must
                spend 5 times the amount claimed.
              </li>

              <li>
                Your shipping Credits will normally be available to use within
                24 hours of the qualifying spending requirement is met.
              </li>

              <li>
                You may have more than one promotional offer available on your
                account at any one time. However, qualifying settlements will
                only count toward releasing one of the offers. You may choose
                which offer is active within the My Offers section of the
                Account Menu.
              </li>
            </ul>
            <p>
              <b>Using shipping Credits:</b>
            </p>
            <p>
              Your shipping Credits are non-withdrawable or refundable. Once the
              Spending requirement limit is met, shipping Credits will be added
              to your available Balance and to using in purchasing postage.
            </p>
            <p>
              <b>General:</b>
            </p>
            <p>
              This offer closes to new customers at{" "}
              <b>
                {getDateTime(true)} (ie., 30 days from the date of registration){" "}
              </b>
              and can only be claimed by customers who register before this time
              (must be claimed within 30 days of registration).
            </p>
            <p>
              Where any term of the offer or promotion is breached or there is
              any evidence of a series abused by a customer or group of
              customers, which due to enhanced payments or any other promotional
              offer results in guaranteed customer profits irrespective of the
              outcome, whether individually or as part of a group, ShipVista.com
              may reclaim the enhanced payment or shipping Credit element of
              such offers and/or void any shipping credits offered or shipments
              created. In addition, where there is evidence of such activity,
              ShipVista.com may levy an administration charge on the customer up
              to the value of the shipping Credits or enhanced payment towards
              the administrative costs incurred in identifying and taking action
              against the activity.
            </p>
            <p>
              ShipVista.com may reclaim any bonus amount, shipping Credits,
              refund amount, or enhanced payments that have been awarded in
              error.
            </p>
            <p>
              All customer offers are limited to one per person. If
              ShipVista.com has reasonable grounds to suspect that the bonus or
              offer is being claimed by or for the benefit of the same person
              more than once or by a group of people then it may withdraw the
              availability of any offer or all offers to that customer or group
              of customers and/or void any credits funded by the bonus or offer
              and remove any shipment postage purchase from such credits.
            </p>
            <p>
              ShipVista.com may, at any time, make minor amendments to this
              promotion to correct typographical errors or to improve on clarity
              or customer experience and may cancel this promotion for legal or
              regulatory reasons.
            </p>
            <p>
              Employees, officers, and directors of ShipVista.com, its
              promotional or other agencies, licensees and licensors, service
              providers, and any other associated or affiliated companies shall
              not be eligible for the promotion. The same terms shall apply to
              the direct families of such persons.
            </p>
          </div>
          <div>
            <h4>How to Claim your Offer? </h4>
            <br />
            <div>
              {/* @ts-ignore */}
              <Accordion open={open} toggle={toggle}>
                {howItWorks?.map((h, i) => (
                  <AccordionItem key={i}>
                    <AccordionHeader targetId={(i + 1).toString()}>
                      {i + 1}. {h.title}
                    </AccordionHeader>
                    <AccordionBody accordionId={(i + 1).toString()}>
                      <Image
                        src={h.imageUrl}
                        alt={`New User Offer Step-${i + 1}`}
                        className="img-fluid"
                        width={1000}
                        height={800}
                      />
                    </AccordionBody>
                  </AccordionItem>
                ))}
              </Accordion>
            </div>
            {/* <ol>
          {howItWorks.map((h, i) => (
            <li key={i}>
              <p>{h.title}</p>
              <img
                src={h.imageUrl}
                alt={`New User Offer Step-${i + 1}`}
                className="img-fluid"
                // width={600}
                // height={400}
              />
              <br />
            </li>
          ))}
        </ol> */}
          </div>
          <Col className="my-4" item xs={12}>
            <h4>Other FAQ&#39;s</h4>
            <FAQAccordion faqList={OPEN_ACCOUNT_FAQ} />
          </Col>
        </section>
        <p>
          <strong>Last updated:</strong> April 4th, 2023
        </p>
      </Container>
    </main>
  );
}
